import { computed, type Ref } from "vue";
import { useQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import type { ApiError } from "@/helpers/api-error";

const BASE = `${import.meta.env.VITE_API_URL}/publisher/regression`;

// Mirrors RunItemStatus / RegressionRunStatus on the backend.
export type RunItemStatus = "Pending" | "Running" | "Passed" | "Failed" | "Errored";
export type RegressionRunStatus = "Running" | "Completed";

export interface RegressionRunItem {
    savedCallId: string;
    savedCallName: string;
    workflowRunId: string;
    status: RunItemStatus;
    validationResultId: string | null;
    message: string | null;
}

export interface RegressionRun {
    id: string;
    orgId: string;
    suiteId: string | null;
    startedAtUtc: string;
    status: RegressionRunStatus;
    passedCount: number;
    failedCount: number;
    pendingCount: number;
    items: RegressionRunItem[];
}

export interface RegressionSuite {
    id: string;
    name: string;
    savedCallIds: string[];
    createdAtUtc: string;
    updatedAtUtc: string;
}

// A batch is "actively converging" only while it's non-terminal AND recent. The recency bound stops
// a permanently-stuck batch (e.g. a lost completion event) from polling — and re-rendering the list —
// forever. Ten minutes comfortably covers any real batch; beyond that we treat it as settled.
const ACTIVE_WINDOW_MS = 10 * 60 * 1000;

function isActivelyRunning(run: RegressionRun): boolean {
    if (run.status === "Completed") return false;
    const startedMs = Date.parse(run.startedAtUtc);
    return Number.isNaN(startedMs) || Date.now() - startedMs < ACTIVE_WINDOW_MS;
}

// READ — batch runs (newest first). Pass a projectId ref to scope to runs that exercise a project (the
// per-project Testing tab); omit it for the org-wide hub. Polls only while a batch is actively
// converging (non-terminal + recent), so collapsed rows' pass/fail badges settle without the list
// refetching indefinitely.
export function useRegressionRuns(projectId?: Ref<string | undefined>) {
    const { data, isFetching: runsLoading, error, refetch: refetchRuns } = useQuery({
        queryKey: computed(() => ["regression-runs", projectId?.value ?? null]),
        queryFn: () => {
            const qs = projectId?.value ? `?projectId=${encodeURIComponent(projectId.value)}` : "";
            return fetchWrapper.get<RegressionRun[]>(`${BASE}/runs${qs}`);
        },
        refetchInterval: (query) => {
            const runs = query.state.data as RegressionRun[] | undefined;
            return runs?.some(isActivelyRunning) ? 5000 : false;
        },
    });
    const runs = computed(() => data.value ?? []);
    return { runs, runsLoading, runsError: error as Ref<ApiError | null>, refetchRuns };
}

// READ — a single batch, polled while it's still Running so the UI shows items converging.
export function useRegressionRun(regressionRunId: Ref<string | null>) {
    const queryKey = computed(() => ["regression-run", regressionRunId.value]);
    const { data: run, isFetching: runLoading, error } = useQuery({
        queryKey,
        queryFn: () => fetchWrapper.get<RegressionRun>(`${BASE}/runs/${regressionRunId.value}`),
        enabled: computed(() => !!regressionRunId.value),
        // Poll while the batch is actively converging (non-terminal + recent), then stop — so an
        // expanded stuck batch doesn't poll forever.
        refetchInterval: (query) => {
            const run = query.state.data as RegressionRun | undefined;
            return run && isActivelyRunning(run) ? 5000 : false;
        },
    });
    return { run, runLoading, runError: error as Ref<ApiError | null> };
}

// READ — named suites. Pass a projectId ref to scope to suites that contain the project's calls (the
// per-project Testing tab); omit it for the org-wide hub.
export function useRegressionSuites(projectId?: Ref<string | undefined>) {
    const { data, isFetching: suitesLoading, error } = useQuery({
        queryKey: computed(() => ["regression-suites", projectId?.value ?? null]),
        queryFn: () => {
            const qs = projectId?.value ? `?projectId=${encodeURIComponent(projectId.value)}` : "";
            return fetchWrapper.get<RegressionSuite[]>(`${BASE}/suites${qs}`);
        },
    });
    const suites = computed(() => data.value ?? []);
    return { suites, suitesLoading, suitesError: error as Ref<ApiError | null> };
}

// WRITE — run a batch. Precedence: explicit savedCallIds (ad-hoc, non-empty) → named suite. Returns
// the batch id.
export function useRunRegression() {
    const queryClient = useQueryClient();
    const { mutate, mutateAsync, isPending, error } = useMutation<
        string,
        ApiError,
        { suiteId?: string; savedCallIds?: string[] } | void
    >({
        mutationFn: (vars) =>
            fetchWrapper.post<string>(`${BASE}/run`, {
                suiteId: vars?.suiteId ?? null,
                savedCallIds: vars?.savedCallIds ?? null,
            }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["regression-runs"] }),
    });
    return {
        runRegression: mutate,
        runRegressionAsync: mutateAsync,
        runRegressionPending: isPending,
        runRegressionError: error as Ref<ApiError | null>,
    };
}

// WRITE — create a named suite from a set of saved-call ids. Returns the new suite id.
export function useCreateRegressionSuite() {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending, error } = useMutation<
        string,
        ApiError,
        { name: string; savedCallIds: string[] }
    >({
        mutationFn: ({ name, savedCallIds }) =>
            fetchWrapper.post<string>(`${BASE}/suites`, { name, savedCallIds }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["regression-suites"] }),
    });
    return {
        createSuiteAsync: mutateAsync,
        createSuitePending: isPending,
        createSuiteError: error as Ref<ApiError | null>,
    };
}

// WRITE — delete a suite.
export function useDeleteRegressionSuite() {
    const queryClient = useQueryClient();
    const { mutateAsync, isPending, error } = useMutation<void, ApiError, string>({
        mutationFn: (suiteId) => fetchWrapper.delete<void>(`${BASE}/suites/${suiteId}`),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["regression-suites"] }),
    });
    return {
        deleteSuiteAsync: mutateAsync,
        deleteSuitePending: isPending,
        deleteSuiteError: error as Ref<ApiError | null>,
    };
}
